import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosInstance';

/**
 * useDraftsApi Hook
 *
 * Handles draft management for requests.
 * Provides CRUD operations for request drafts.
 */
export const useDraftsApi = () => {
  const [state, setState] = useState({
    loading: false,
    drafts: [],
    error: null,
  });

  /**
   * Create or update a draft
   *
   * @param {Object} data - Draft data
   * @param {number} data.workflow_version_id - Workflow version ID
   * @param {number|null} data.request_id - Request ID (null for new requests)
   * @param {number} data.stage_id - Stage ID
   * @param {Object} data.form_data - Form data object
   * @param {string} data.study_name - Optional study name
   * @param {Array<Object>} data.file_data - Array of file data with metadata [{id, formId, subformId, fieldId}]
   * @returns {Object|null} Created/updated draft or null on error
   */
  const createOrUpdateDraft = useCallback(async (data, options = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { showToast = true } = options;
      const payload = {
        workflow_version_id: data.workflow_version_id,
        request_id: data.request_id || null,
        draft_id: data.draft_id || null, // Existing draft ID when editing (enables multiple new-request drafts)
        stage_id: data.stage_id,
        form_data: data.form_data,
        study_name: data.study_name || null,
        file_data: data.file_data || [], // New file module with metadata
        additional_documents_comments:
          data.additional_documents_comments || null,
        linked_request_ids: data.linked_request_ids || null,
        parent_request_ids: data.parent_request_ids || null,
      };

      const response = await axiosInstance.post('/api/v1/drafts', payload);

      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
      }));

      if (showToast) {
        const message = response.data.updated
          ? 'Draft updated successfully'
          : 'Draft saved successfully';
        toast.success(message, { position: 'top-center' });
      }

      return response.data.draft;
    } catch (error) {
      // Handle BaseError format from backend: { error: { type, message, resource } }
      const errorMessage =
        error.response?.data?.error?.message || // BaseError format
        error.response?.data?.message || // Simple message format
        error.message ||
        'Failed to save draft';

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      if (options.showToast !== false) {
        toast.error(errorMessage, { position: 'top-center' });
      }
      return null;
    }
  }, []);

  /**
   * Get all drafts for the current user
   *
   * @returns {Array} List of drafts
   */
  const getUserDrafts = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axiosInstance.get('/api/v1/drafts');

      setState((prev) => ({
        ...prev,
        loading: false,
        drafts: response.data.drafts || [],
        error: null,
      }));

      return response.data.drafts || [];
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch drafts';

      setState((prev) => ({
        ...prev,
        loading: false,
        drafts: [],
        error: errorMessage,
      }));

      return [];
    }
  }, []);

  /**
   * Get a draft by ID
   *
   * @param {number} id - Draft ID
   * @returns {Object|null} Draft data or null on error
   */
  const getDraftById = useCallback(async (id) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axiosInstance.get(`/api/v1/drafts/${id}`);

      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
      }));

      return response.data.draft;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch draft';

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      if (error.response?.status === 404) {
        toast.error('Draft not found', { position: 'top-center' });
      }

      return null;
    }
  }, []);

  /**
   * Delete a draft
   *
   * @param {number} id - Draft ID
   * @returns {boolean} Success status
   */
  const deleteDraft = useCallback(async (id) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await axiosInstance.delete(`/api/v1/drafts/${id}`);

      setState((prev) => ({
        ...prev,
        loading: false,
        drafts: prev.drafts.filter((d) => d.id !== id),
        error: null,
      }));

      toast.success('Draft deleted successfully', { position: 'top-center' });
      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to delete draft';

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      toast.error(errorMessage, { position: 'top-center' });
      return false;
    }
  }, []);

  /**
   * Append deleted file IDs to a draft (persist deletions immediately)
   *
   * @param {number} id - Draft ID
   * @param {Array<number>} deletedFileIds - File IDs to append
   * @returns {Array<number>|null} Updated deleted_file_ids or null on error
   */
  const updateDraftDeletedFiles = useCallback(async (id, deletedFileIds) => {
    if (!id || !Array.isArray(deletedFileIds)) return null;

    try {
      const response = await axiosInstance.patch(
        `/api/v1/drafts/${id}/deleted-files`,
        { deleted_file_ids: deletedFileIds }
      );
      return response.data.deleted_file_ids || [];
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update draft deletions';
      toast.error(errorMessage, { position: 'top-center' });
      return null;
    }
  }, []);

  /**
   * Get draft count for the current user
   *
   * @returns {number} Draft count
   */
  const getDraftCount = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/v1/drafts/count');
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching draft count:', error);
      return 0;
    }
  }, []);

  const resetState = useCallback(() => {
    setState({
      loading: false,
      drafts: [],
      error: null,
    });
  }, []);

  return {
    // State
    loading: state.loading,
    drafts: state.drafts,
    error: state.error,

    // Functions
    createOrUpdateDraft,
    getUserDrafts,
    getDraftById,
    deleteDraft,
    getDraftCount,
    updateDraftDeletedFiles,
    resetState,
  };
};

export default useDraftsApi;
